// A month's margin, customer by customer: what each billed, what reached us,
// what it cost and what it contributed, per conversation and per minute as
// well as in total, with a what-if per customer and each customer's last
// twelve months. The book's month over month is on the Trends page.
//
// Every figure comes from /api/reports/margin and /api/reports/margin-trend.
// The trend is asked for the eleven months BEFORE the shown one, and the shown
// month is added from the month report (marginHistory.ts), so the server works
// the open month out once rather than twice side by side.
// The what-if (marginModel.ts) can change a line's price, the month's
// minutes, and which costs are fixed; nothing it does is saved.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type {
  Currency,
  MarginCustomer,
  MarginReport,
  MarginTrendPoint,
  MarginTrendReport,
} from "@crm/shared";
import styles from "./MarginPage.module.css";
import {
  deriveMargin,
  formatUnits,
  hasEdits,
  marginHealth,
  NO_EDITS,
  prepaidDescription,
  sumMargins,
  unitEconomics,
  unitShortName,
  type CostLineFigures,
  type MarginFigures,
  type MarginHealth,
  type PriceEdits,
  type RevenueLineFigures,
} from "./marginModel.js";
import { MonthlyChart, type ChartSeries } from "../../design/chart/MonthlyChart.js";
import {
  periodToSearch,
  searchToPeriod,
  shiftPeriod,
  type CloseSearch,
} from "../close/closeSearch.js";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card } from "../../design/Card.js";
import { EmptyState, Spinner } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { Table, Td, Th } from "../../design/Table.js";
import { colorForKey, labelForKey } from "../../design/chart/series.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useMarginReport, useMarginTrendRange } from "../../api/queries.js";
import { customerHistory, marginHistoryWindow } from "./marginHistory.js";
import { formatMonth, todayIsoDate } from "../../format/calendarDate.js";
import { formatMoney, formatPercent, formatRate } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { assertNever } from "../../lib/assertNever.js";

const ADJ_MIN = -50;
const ADJ_MAX = 100;

type Money = (minor: number) => string;

export function MarginPage(): ReactNode {
  const search = useSearch({ from: "/margin" });
  const navigate = useNavigate();
  const today = todayIsoDate();
  const period = useMemo(() => searchToPeriod(search, today), [search, today]);
  const setPeriod = useCallback(
    (next: string) => {
      void navigate({ to: "/margin", search: periodToSearch(next, today) as CloseSearch });
    },
    [navigate, today],
  );

  const currency = useReportCurrency();
  const [includeOneTime, setIncludeOneTime] = useState(false);
  const [edits, setEdits] = useState<Record<string, PriceEdits>>({});
  const report = useMarginReport(period, currency);
  const historyWindow = useMemo(() => marginHistoryWindow(period), [period]);
  const history = useMarginTrendRange({
    ...historyWindow,
    currency,
    partnerId: null,
    scope: "rolled_up",
  });
  const busy = report.isFetching;

  usePageChrome({ title: "Margin" });

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.monthPick}>
          <Button
            variant="secondary"
            onClick={() => setPeriod(shiftPeriod(period, -1))}
            aria-label="Previous month"
            disabled={busy}
          >
            ‹
          </Button>
          <span className={styles.monthLabel}>{formatMonth(period)}</span>
          <Button
            variant="secondary"
            onClick={() => setPeriod(shiftPeriod(period, 1))}
            aria-label="Next month"
            disabled={busy}
          >
            ›
          </Button>
        </div>
        {report.isPlaceholderData && <Spinner label={`Loading ${formatMonth(period)}…`} inline />}
        <span className={styles.spacer} />
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={includeOneTime}
            onChange={(event) => setIncludeOneTime(event.target.checked)}
          />
          Include one-time fees
        </label>
        <Button
          variant="subtle"
          onClick={() => setEdits({})}
          disabled={!Object.values(edits).some(hasEdits)}
        >
          Reset all
        </Button>
      </div>

      <QueryBoundary query={report} label="the margin report">
        {(data) => (
          <div className={report.isPlaceholderData ? styles.stale : styles.results}>
            <MarginBody
              report={data}
              history={history.data}
              includeOneTime={includeOneTime}
              edits={edits}
              onEdit={(dealId, next) => setEdits((current) => ({ ...current, [dealId]: next }))}
            />
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}

interface MarginBodyProps {
  report: MarginReport;
  /** The months before the report's; undefined while it loads. */
  history: MarginTrendReport | undefined;
  includeOneTime: boolean;
  edits: Record<string, PriceEdits>;
  onEdit: (dealId: string, next: PriceEdits) => void;
}

function MarginBody({
  report,
  history,
  includeOneTime,
  edits,
  onEdit,
}: MarginBodyProps): ReactNode {
  const trends = useMemo(() => customerHistory(history, report), [history, report]);
  const rows = useMemo(
    () =>
      report.customers.map((customer) => {
        const edit = edits[customer.deal_id] ?? NO_EDITS;
        return { customer, edit, figures: deriveMargin(customer, edit, includeOneTime) };
      }),
    [report.customers, edits, includeOneTime],
  );
  const sums = useMemo(() => sumMargins(rows.map((row) => row.figures)), [rows]);
  const money: Money = (minor) => formatMoney(minor, report.currency, { omitCurrencyCode: true });

  if (rows.length === 0) {
    return (
      <EmptyState
        title={`Nothing billed or measured in ${formatMonth(report.params.period)}`}
        description="No revenue clause covers the month and no bound tenant used anything."
      />
    );
  }

  return (
    <>
      <StatTileRow>
        <StatTile
          label="Billed"
          value={money(sums.billedMinor)}
          hint={`gross, pre-tax ${report.currency}`}
        />
        <StatTile
          label="Our revenue"
          value={money(sums.oursMinor)}
          hint={
            sums.billedMinor === 0
              ? "after partner share"
              : `${formatPercent((sums.oursMinor * 100) / sums.billedMinor, 1)} of billed`
          }
        />
        <StatTile
          label="Cost we carry"
          value={money(sums.ourCostMinor)}
          hint={`${money(sums.costMinor)} caused · partners carry ${money(sums.partnerCostMinor)}`}
        />
        <StatTile
          label="Contribution"
          value={money(sums.contributionMinor)}
          hint="our revenue less cost we carry"
        />
        <StatTile
          label="Margin"
          value={sums.marginPct === null ? null : formatPercent(sums.marginPct, 1)}
          hint={
            sums.withoutCost === 0
              ? "on our revenue"
              : `${sums.withoutCost} customer(s) with no cost left out`
          }
        />
      </StatTileRow>

      <div className={styles.cards}>
        {rows.map(({ customer, edit, figures }) => (
          <CustomerCard
            key={customer.deal_id}
            customer={customer}
            edit={edit}
            figures={figures}
            includeOneTime={includeOneTime}
            currency={report.currency}
            trend={trends?.get(customer.deal_id)}
            money={money}
            onEdit={(next) => onEdit(customer.deal_id, next)}
          />
        ))}
      </div>

      <Card>
        <Table label="Margin by customer">
          <thead>
            <tr>
              <Th>Customer</Th>
              <Th numeric>Price change</Th>
              <Th numeric>Billed</Th>
              <Th numeric>Our revenue</Th>
              <Th numeric>Cost caused</Th>
              <Th numeric>Partner carries</Th>
              <Th numeric>We carry</Th>
              <Th numeric>Contribution</Th>
              <Th numeric>Margin</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ customer, edit, figures }) => (
              <tr key={customer.deal_id}>
                <Td>
                  {customer.customer_name}{" "}
                  <span className={styles.muted}>{customer.partner_name}</span>
                </Td>
                <Td numeric>{priceChangeLabel(customer, edit, figures)}</Td>
                <Td numeric>{money(figures.billedMinor)}</Td>
                <Td numeric>{money(figures.oursMinor)}</Td>
                <Td numeric>{figures.costMinor === null ? "—" : money(figures.costMinor)}</Td>
                <Td numeric>
                  {figures.partnerCostMinor === null || figures.partnerCostMinor === 0
                    ? "—"
                    : money(figures.partnerCostMinor)}
                </Td>
                <Td numeric>{figures.ourCostMinor === null ? "—" : money(figures.ourCostMinor)}</Td>
                <Td numeric>
                  <Signed minor={figures.contributionMinor} money={money} />
                </Td>
                <Td numeric>
                  {figures.marginPct === null ? "—" : formatPercent(figures.marginPct, 1)}
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td>Total</Td>
              <Td />
              <Td numeric>{money(sums.billedMinor)}</Td>
              <Td numeric>{money(sums.oursMinor)}</Td>
              <Td numeric>{money(sums.costMinor)}</Td>
              <Td numeric>{money(sums.partnerCostMinor)}</Td>
              <Td numeric>{money(sums.ourCostMinor)}</Td>
              <Td numeric>
                <Signed minor={sums.contributionMinor} money={money} />
              </Td>
              <Td numeric>{sums.marginPct === null ? "—" : formatPercent(sums.marginPct, 1)}</Td>
            </tr>
          </tfoot>
        </Table>
      </Card>

      <div className={styles.footnote}>
        {report.fx !== null && (
          <span>
            1 USD = {formatRate(report.fx.usd_to_cad)} CAD, as of {report.fx.as_of}
          </span>
        )}
        {report.metered_computed_at !== null && (
          <span>
            Metered usage rolled up {report.metered_computed_at.slice(0, 16).replace("T", " ")} UTC
          </span>
        )}
      </div>
    </>
  );
}

interface CustomerCardProps {
  customer: MarginCustomer;
  edit: PriceEdits;
  figures: MarginFigures;
  includeOneTime: boolean;
  /** The report currency every figure is in; the deal may bill in another. */
  currency: Currency;
  trend: MarginTrendPoint[] | undefined;
  money: Money;
  onEdit: (next: PriceEdits) => void;
}

function CustomerCard({
  customer,
  edit,
  figures,
  includeOneTime,
  currency,
  trend,
  money,
  onEdit,
}: CustomerCardProps): ReactNode {
  const health = marginHealth(figures);
  const base = deriveMargin(customer, NO_EDITS, includeOneTime);
  const sliderId = `adj-${customer.deal_id}`;
  const edited = hasEdits(edit);
  const partnerPct = customer.revenue_share_pct === null ? null : 100 - customer.revenue_share_pct;
  const typedCount = Object.keys(edit.lineGross).length + Object.keys(edit.lineRate).length;

  return (
    <Card className={styles.card}>
      <div className={styles.cardTop}>
        <div>
          <div className={styles.partner}>{customer.partner_name}</div>
          <div className={styles.customer}>{customer.customer_name}</div>
          <div className={styles.terms}>
            {customer.revenue_share_pct === null
              ? "no recurring revenue"
              : `${formatPercent(customer.revenue_share_pct, 1)} ours`}
            {customer.deal_currency !== currency && ` · billed in ${customer.deal_currency}`}
            {customer.unpriced_revenue_line_count > 0 &&
              ` · ${customer.unpriced_revenue_line_count} revenue line(s) unpriced`}
          </div>
        </div>
        <div className={styles.cardActions}>
          {/* Always there, so a reader knows the card can be put back; it resets this deal only. */}
          <Button
            variant="secondary"
            onClick={() => onEdit(NO_EDITS)}
            disabled={!edited}
            aria-label={`Reset ${customer.customer_name} to actual prices`}
          >
            Reset to actuals
          </Button>
          <Badge tone={healthTone(health)}>{healthLabel(health)}</Badge>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.costs}>
          <div className={styles.panelHead}>
            <span className={styles.eyebrow}>Revenue</span>
            <span className="mono">{money(figures.billedMinor)}</span>
          </div>
          <RevenueBreakdown
            customer={customer}
            lines={figures.lines}
            money={money}
            onPrice={(line, valueMinor) => {
              const field = line.priceKind === "rate" ? "lineRate" : "lineGross";
              const next = { ...edit[field] };
              if (valueMinor === null) delete next[line.key];
              else next[line.key] = valueMinor;
              onEdit({ ...edit, [field]: next });
            }}
          />

          <div className={`${styles.panelHead} ${styles.panelGap}`}>
            <span className={styles.eyebrow}>{costHeading(customer)}</span>
            <span className="mono">
              {figures.costMinor === null ? "—" : money(figures.costMinor)}
            </span>
          </div>
          <CostBreakdown
            customer={customer}
            costs={figures.costs}
            scalable={figures.baseMinutes !== null && figures.baseMinutes > 0}
            money={money}
            onToggleFixed={(key) => {
              const fixedCosts = { ...edit.fixedCosts };
              if (fixedCosts[key] === true) delete fixedCosts[key];
              else fixedCosts[key] = true;
              onEdit({ ...edit, fixedCosts });
            }}
          />
        </div>

        <div className={styles.margin}>
          <div className={styles.bigMargin}>
            <span
              className={`mono ${figures.contributionMinor !== null && figures.contributionMinor < 0 ? styles.neg : ""}`}
            >
              {figures.marginPct === null ? "—" : formatPercent(figures.marginPct, 1)}
            </span>
            {edited && figures.marginPct !== null && base.marginPct !== null && (
              <span
                className={`mono ${figures.marginPct >= base.marginPct ? styles.pos : styles.neg}`}
              >
                {figures.marginPct >= base.marginPct ? "+" : "−"}
                {Math.abs(figures.marginPct - base.marginPct).toFixed(1)} pts
              </span>
            )}
          </div>

          <Volume customer={customer} edit={edit} figures={figures} onEdit={onEdit} />

          {customer.recurring_our_share_minor > 0 && (
            <div className={styles.slider}>
              <label htmlFor={sliderId} className={styles.sliderLabel}>
                <span>
                  {typedCount > 0
                    ? "Price change, lines with no typed price"
                    : "Price change, every recurring line"}
                </span>
                <span className="mono">
                  {edit.adjPct > 0 ? "+" : ""}
                  {edit.adjPct}%
                </span>
              </label>
              <input
                id={sliderId}
                type="range"
                min={ADJ_MIN}
                max={ADJ_MAX}
                step={1}
                value={edit.adjPct}
                onChange={(event) => onEdit({ ...edit, adjPct: Number(event.target.value) })}
              />
            </div>
          )}

          <dl className={styles.waterfall}>
            <dt>Billed</dt>
            <dd className="mono">{money(figures.billedMinor)}</dd>
            <dt className={styles.muted}>
              {partnerPct === null || partnerPct < 0.05
                ? "no partner share"
                : `less ${customer.partner_name}'s ${formatPercent(partnerPct, 1)}`}
            </dt>
            <dd className={`mono ${styles.muted}`}>
              {figures.partnerShareMinor === 0 ? "—" : `−${money(figures.partnerShareMinor)}`}
            </dd>
            <dt>Our revenue</dt>
            <dd className="mono">{money(figures.oursMinor)}</dd>
            <dt className={styles.muted}>less cost we carry</dt>
            <dd className={`mono ${styles.muted}`}>
              {figures.ourCostMinor === null ? "—" : `−${money(figures.ourCostMinor)}`}
            </dd>
            <dt className={styles.total}>Contribution</dt>
            <dd className={`mono ${styles.total}`}>
              <Signed minor={figures.contributionMinor} money={money} />
            </dd>
          </dl>

          {figures.breakEvenPct !== null && (
            <div className={styles.breakEven}>
              {figures.breakEvenPct <= -100
                ? "Covers its cost at any price"
                : `Break-even at ${figures.breakEvenPct >= 0 ? "+" : "−"}${Math.abs(figures.breakEvenPct).toFixed(1)}%` +
                  (figures.breakEvenGrossMinor === null
                    ? ""
                    : ` · ${money(figures.breakEvenGrossMinor)} a month billed`)}
            </div>
          )}
          <CustomerTrend customer={customer} points={trend} />
        </div>
      </div>
    </Card>
  );
}

/**
 * The customer's margin over the last twelve months, as it was: no what-if
 * reaches it. Months before the customer billed anything are left off, and a
 * month with no cost on file is marked as such rather than drawn as a margin.
 */
function CustomerTrend({
  customer,
  points,
}: {
  customer: MarginCustomer;
  points: MarginTrendPoint[] | undefined;
}): ReactNode {
  if (points === undefined) return null;
  const first = points.findIndex(
    (point) => point.recurring_our_share_minor !== 0 || point.margin_pct !== null,
  );
  const shown = first < 0 ? [] : points.slice(first);
  const known = shown.filter((point) => point.margin_pct !== null);
  const latest = known.at(-1);
  const previous = known.at(-2);

  return (
    <div className={styles.trend}>
      <div className={styles.panelHead}>
        <span className={styles.eyebrow}>Margin by month</span>
        {latest !== undefined && latest.margin_pct !== null && (
          <span className={styles.trendSummary}>
            {`${formatPercent(latest.margin_pct, 1)} in ${formatMonth(latest.period)}`}
            {previous !== undefined && previous.margin_pct !== null && (
              <span className={latest.margin_pct >= previous.margin_pct ? styles.pos : styles.neg}>
                {` · ${latest.margin_pct >= previous.margin_pct ? "+" : "−"}${Math.abs(
                  latest.margin_pct - previous.margin_pct,
                ).toFixed(1)} pts on ${formatMonth(previous.period)}`}
              </span>
            )}
          </span>
        )}
      </div>
      {known.length < 2 ? (
        <div className={styles.muted}>
          {known.length === 0
            ? "No month with a cost on file yet, so no margin history."
            : "Only one month with a margin so far; the chart starts with the second."}
        </div>
      ) : (
        <MonthlyChart
          label={`${customer.customer_name} margin by month`}
          months={shown.map((point) => point.period)}
          series={trendSeries(shown)}
          formatValue={(value) => formatPercent(value, 1)}
          formatTick={(value) => formatPercent(value, 0)}
          caveats={shown.map((point) => (point.margin_pct === null ? "No cost on file" : null))}
        />
      )}
    </div>
  );
}

function trendSeries(points: readonly MarginTrendPoint[]): ChartSeries[] {
  return [
    {
      key: "margin",
      label: "Margin",
      kind: "line",
      slot: 1,
      values: points.map((point) => point.margin_pct ?? 0),
    },
  ];
}

/** The month's volume, editable, and what the customer is worth per unit of it. */
function Volume({
  customer,
  edit,
  figures,
  onEdit,
}: {
  customer: MarginCustomer;
  edit: PriceEdits;
  figures: MarginFigures;
  onEdit: (next: PriceEdits) => void;
}): ReactNode {
  if (figures.minutes === null || figures.baseMinutes === null) {
    return <div className={styles.muted}>No tenant bound, so no volume to show.</div>;
  }
  const unit = unitEconomics(figures);
  const perUnit = (minor: number | null): string => (minor === null ? "—" : unitMoney(minor));
  return (
    <div className={styles.volume}>
      <div className={styles.volumeHead}>
        <span className={styles.rateCell}>
          <NumberInput
            label={`${customer.customer_name} minutes`}
            value={figures.minutes}
            decimals={0}
            onChange={(value) => onEdit({ ...edit, minutes: value })}
          />
          <span className={styles.muted}>min</span>
        </span>
        <span className={`mono ${styles.muted}`}>
          {figures.conversations === null
            ? ""
            : `${formatUnits(figures.conversations)} conversations`}
        </span>
        <span className={`mono ${styles.was}`}>
          {edit.minutes !== null ? `was ${formatUnits(figures.baseMinutes)} min` : ""}
        </span>
      </div>
      <dl className={styles.unitGrid}>
        <dt>Cost / conversation</dt>
        <dd className="mono">{perUnit(unit.costPerConversationMinor)}</dd>
        <dt>Cost / min</dt>
        <dd className="mono">{perUnit(unit.costPerMinuteMinor)}</dd>
        <dt>Our revenue / min</dt>
        <dd className="mono">{perUnit(unit.oursPerMinuteMinor)}</dd>
        <dt>Contribution / min</dt>
        <dd className="mono">{perUnit(unit.contributionPerMinuteMinor)}</dd>
      </dl>
    </div>
  );
}

/** What the price-change column says: how far the recurring billing moved. */
function priceChangeLabel(
  customer: MarginCustomer,
  edit: PriceEdits,
  figures: MarginFigures,
): string {
  if (!hasEdits(edit)) return "—";
  const base = customer.recurring_gross_minor;
  const now = figures.lines
    .filter((line) => line.recurring)
    .reduce((sum, line) => sum + line.grossMinor, 0);
  if (base === 0) return "edited";
  const pct = ((now - base) * 100) / base;
  return `${pct >= 0 ? "+" : "−"}${formatPercent(Math.abs(pct), 1)}`;
}

function RevenueBreakdown({
  customer,
  lines,
  money,
  onPrice,
}: {
  customer: MarginCustomer;
  lines: readonly RevenueLineFigures[];
  money: Money;
  onPrice: (line: RevenueLineFigures, valueMinor: number | null) => void;
}): ReactNode {
  if (lines.length === 0) {
    return (
      <div className={styles.muted}>
        {customer.unpriced_revenue_line_count > 0
          ? "Revenue lines have no figure this month."
          : "No revenue this month."}
      </div>
    );
  }
  return (
    <div className={styles.costList}>
      {lines.map((line) => {
        const description = prepaidDescription(line.source, unitMoney);
        const unit = unitShortName(line.unit);
        return (
          <div key={line.key} className={styles.revenueLine}>
            <div className={styles.revenueRow}>
              <span className={styles.costName} title={line.label}>
                {line.label}
                {line.fromPool && <span className={styles.tag}>pool share</span>}
                {!line.recurring && <span className={styles.tag}>one-time</span>}
              </span>
              {line.recurring && line.priceKind === "rate" ? (
                <span className={styles.rateCell}>
                  <NumberInput
                    label={`${customer.customer_name} ${line.label} price per ${unit}`}
                    value={(line.rateMinor ?? 0) / 100}
                    decimals={4}
                    prefix="$"
                    onChange={(value) => onPrice(line, value === null ? null : value * 100)}
                  />
                  <span className={styles.muted}>/{unit}</span>
                </span>
              ) : line.recurring ? (
                <NumberInput
                  label={`${customer.customer_name} ${line.label} price`}
                  value={line.grossMinor / 100}
                  decimals={2}
                  prefix="$"
                  onChange={(value) =>
                    onPrice(line, value === null ? null : Math.round(value * 100))
                  }
                />
              ) : (
                <span className="mono">{money(line.grossMinor)}</span>
              )}
              <span className={`mono ${styles.was}`}>
                {line.priceKind === "rate"
                  ? `${formatUnits(line.quantity ?? 0)} ${unit} · ${money(line.grossMinor)}`
                  : line.grossMinor !== line.baseGrossMinor
                    ? `was ${money(line.baseGrossMinor)}`
                    : ""}
              </span>
              <span
                className={`mono ${styles.muted}`}
              >{`${formatPercent(line.sharePct, 1)} ours`}</span>
            </div>
            {description !== null && <div className={styles.lineNote}>{description}</div>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A number typed by the reader. The text is theirs while they type; it follows
 * the figure again once they leave. An empty box clears the edit.
 */
function NumberInput({
  label,
  value,
  decimals,
  prefix,
  onChange,
}: {
  label: string;
  value: number;
  decimals: number;
  prefix?: string;
  onChange: (value: number | null) => void;
}): ReactNode {
  const shown = trimNumber(value, decimals);
  const [text, setText] = useState(shown);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(shown);
  }, [shown, editing]);
  return (
    <span className={styles.priceInput}>
      {prefix !== undefined && <span className={styles.muted}>{prefix}</span>}
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        aria-label={label}
        value={text}
        onFocus={() => setEditing(true)}
        onBlur={() => setEditing(false)}
        onChange={(event) => {
          setText(event.target.value);
          const raw = event.target.value.trim();
          if (raw === "") return onChange(null);
          const parsed = Number(raw);
          if (Number.isFinite(parsed) && parsed >= 0) onChange(parsed);
        }}
      />
    </span>
  );
}

function trimNumber(value: number, decimals: number): string {
  const fixed = value.toFixed(decimals);
  return fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;
}

/** A price per unit: fractions of a cent matter, so it keeps up to four decimals. */
function unitMoney(minor: number): string {
  const major = minor / 100;
  const text = trimNumber(Math.abs(major), Math.abs(major) < 1 ? 4 : 2);
  const [whole, fraction = ""] = text.split(".");
  return `${major < 0 ? "−" : ""}$${whole}.${fraction.padEnd(2, "0")}`;
}

function CostBreakdown({
  customer,
  costs,
  scalable,
  money,
  onToggleFixed,
}: {
  customer: MarginCustomer;
  costs: readonly CostLineFigures[];
  scalable: boolean;
  money: Money;
  onToggleFixed: (key: string) => void;
}): ReactNode {
  if (customer.cost_basis === "none") {
    return <div className={styles.muted}>No cost on file for this customer.</div>;
  }
  const total = costs.reduce((sum, line) => sum + line.costMinor, 0);
  const lines = customer.cost_lines.map((line, index) => ({
    key: line.key,
    now: costs[index],
    color: colorForKey(line.key, index),
    name: customer.cost_basis === "metered" ? labelForKey(line.key) : line.label,
  }));
  return (
    <>
      {total > 0 && (
        <div className={styles.stack} role="img" aria-label="Cost split">
          {lines.map((line) => (
            <span
              key={line.key}
              style={{
                width: `${((line.now?.costMinor ?? 0) * 100) / total}%`,
                background: line.color,
              }}
              title={`${line.name} — ${money(line.now?.costMinor ?? 0)}`}
            />
          ))}
        </div>
      )}
      <div className={styles.costList}>
        {lines.map((line) => {
          const fixed = line.now?.fixed === true;
          return (
            <div key={line.key} className={styles.costRow}>
              <span className={styles.costName} title={line.name}>
                <i style={{ background: line.color }} />
                {line.name}
              </span>
              {scalable ? (
                <button
                  type="button"
                  className={styles.fixedToggle}
                  aria-pressed={fixed}
                  aria-label={`${line.name}: ${fixed ? "fixed" : "scales with minutes"}`}
                  title={
                    fixed
                      ? "Fixed: stays the same when minutes change. Click to scale it with minutes."
                      : "Scales with minutes. Click to hold it fixed."
                  }
                  onClick={() => onToggleFixed(line.key)}
                >
                  {fixed ? "fixed" : "per min"}
                </button>
              ) : (
                <span />
              )}
              <span className="mono">{money(line.now?.costMinor ?? 0)}</span>
              <span className={`mono ${styles.muted}`}>
                {total === 0 ? "—" : formatPercent(((line.now?.costMinor ?? 0) * 100) / total, 1)}
              </span>
            </div>
          );
        })}
      </div>
      {customer.unpriced_cost_line_count > 0 && (
        <div className={styles.warn}>
          {customer.unpriced_cost_line_count} cost line(s) with no figure
        </div>
      )}
    </>
  );
}

function costHeading(customer: MarginCustomer): string {
  switch (customer.cost_basis) {
    case "recharged":
      return "Cost caused";
    case "metered":
      return "Cost caused · metered usage";
    case "none":
      return "Cost caused";
    default:
      return assertNever(customer.cost_basis, "cost basis");
  }
}

function Signed({ minor, money }: { minor: number | null; money: Money }): ReactNode {
  if (minor === null) return "—";
  return <span className={minor < 0 ? styles.neg : styles.pos}>{money(minor)}</span>;
}

function healthTone(health: MarginHealth): Tone {
  switch (health) {
    case "healthy":
      return "good";
    case "thin":
      return "warn";
    case "breakEven":
    case "losing":
      return "bad";
    case "unknown":
      return "neutral";
    default:
      return assertNever(health, "margin health");
  }
}

function healthLabel(health: MarginHealth): string {
  switch (health) {
    case "healthy":
      return "Healthy margin";
    case "thin":
      return "Thin margin";
    case "breakEven":
      return "Break-even";
    case "losing":
      return "Losing money";
    case "unknown":
      return "No cost on file";
    default:
      return assertNever(health, "margin health");
  }
}
